import { useEffect, useMemo, useState } from "react";
import { api } from "../../App";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Badge } from "../../components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import { Switch } from "../../components/ui/switch";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "../../components/ui/sheet";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/ui/table";
import {
  Globe2,
  Users,
  CalendarDays,
  Repeat,
  TrendingUp,
  Activity,
  MapPin,
  ChevronLeft,
  ChevronRight,
  Download,
  Search,
  Loader2,
  ExternalLink,
  Bell,
  BellOff,
  Settings2,
} from "lucide-react";
import {
  Area,
  AreaChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
  BarChart,
  Bar,
  Cell,
} from "recharts";
import { toast } from "sonner";

const SOURCE_OPTIONS = [
  "google",
  "facebook",
  "instagram",
  "tiktok",
  "twitter",
  "youtube",
  "linkedin",
  "bing",
  "duckduckgo",
  "whatsapp",
  "reddit",
  "direct",
  "internal",
];
const DEVICE_OPTIONS = ["mobile", "tablet", "desktop"];
const PAGE_SIZE = 50;

function StatCard({ icon: Icon, label, value, accent = "bg-neutral-100 text-neutral-700" }) {
  return (
    <div className="card-base flex flex-col gap-1.5">
      <span className={`inline-flex w-9 h-9 rounded-xl items-center justify-center ${accent}`}>
        <Icon className="w-4 h-4" />
      </span>
      <p className="font-heading font-bold text-2xl text-[#111111] leading-none mt-1">
        {value ?? "—"}
      </p>
      <p className="text-xs uppercase tracking-wide text-neutral-500 font-semibold">
        {label}
      </p>
    </div>
  );
}

function BreakdownBars({ items, colors = ["#0EA5E9"], truncate = false }) {
  if (!items?.length) {
    return <p className="text-xs text-neutral-400 py-6 text-center">Sin datos</p>;
  }
  const max = Math.max(...items.map((i) => i.value || 0), 1);
  return (
    <ul className="space-y-2">
      {items.map((it, idx) => {
        const pct = (it.value / max) * 100;
        const color = colors[idx % colors.length];
        return (
          <li key={(it.label || "—") + idx} className="flex items-center gap-2 text-xs">
            <span
              className={`w-32 shrink-0 truncate ${truncate ? "font-mono text-[11px]" : ""}`}
              title={it.label}
            >
              {it.label || "—"}
            </span>
            <div className="flex-1 h-2 bg-neutral-100 rounded-full overflow-hidden">
              <div
                className="h-full rounded-full transition-all"
                style={{ width: `${Math.max(4, pct)}%`, backgroundColor: color }}
              />
            </div>
            <span className="w-10 text-right tabular-nums font-semibold text-neutral-700">
              {it.value}
            </span>
          </li>
        );
      })}
    </ul>
  );
}

const fmtTs = (ts) => {
  if (!ts) return "—";
  try {
    return new Date(ts).toLocaleString("es-MX", {
      year: "2-digit",
      month: "short",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return ts;
  }
};

export default function AdminAnalyticsTab() {
  const [overview, setOverview] = useState(null);
  const [overviewLoading, setOverviewLoading] = useState(true);
  const [timeseries, setTimeseries] = useState([]);
  const [breakdownSource, setBreakdownSource] = useState([]);
  const [breakdownDevice, setBreakdownDevice] = useState([]);
  const [breakdownCity, setBreakdownCity] = useState([]);
  const [breakdownPath, setBreakdownPath] = useState([]);
  const [chartsLoading, setChartsLoading] = useState(true);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({
    start: "",
    end: "",
    country: "",
    source: "",
    device: "",
    audience: "all", // all | logged | anonymous
    search: "",
  });
  const [searchInput, setSearchInput] = useState("");
  const [detail, setDetail] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);

  // Live pulse + admin notification settings
  const [live, setLive] = useState({ events_5m: 0, unique_ips_5m: 0, unique_ips_60m: 0 });
  const [notifyOpen, setNotifyOpen] = useState(false);
  const [notifySettings, setNotifySettings] = useState({
    enabled: false,
    email: "",
    min_interval_minutes: 5,
  });
  const [notifySaving, setNotifySaving] = useState(false);

  // Poll the live pulse every 15s
  useEffect(() => {
    let cancel = false;
    const fetchLive = () =>
      api
        .get("/admin/analytics/live")
        .then((res) => !cancel && setLive(res.data))
        .catch(() => {});
    fetchLive();
    const t = setInterval(fetchLive, 15000);
    return () => {
      cancel = true;
      clearInterval(t);
    };
  }, []);

  // Load notification settings on mount
  useEffect(() => {
    api
      .get("/admin/analytics/notify-settings")
      .then((res) => setNotifySettings(res.data))
      .catch(() => {});
  }, []);

  const saveNotifySettings = async () => {
    setNotifySaving(true);
    try {
      const res = await api.put("/admin/analytics/notify-settings", notifySettings);
      setNotifySettings(res.data);
      toast.success("Notificaciones actualizadas");
      setNotifyOpen(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error guardando ajustes");
    } finally {
      setNotifySaving(false);
    }
  };

  const params = useMemo(() => {
    const p = { page, page_size: PAGE_SIZE };
    if (filters.start) p.start = new Date(filters.start).toISOString();
    if (filters.end) p.end = new Date(filters.end).toISOString();
    if (filters.country) p.country = filters.country;
    if (filters.source) p.source = filters.source;
    if (filters.device) p.device = filters.device;
    if (filters.audience === "logged") p.only_logged_in = true;
    if (filters.audience === "anonymous") p.only_anonymous = true;
    if (filters.search) p.search = filters.search;
    return p;
  }, [filters, page]);

  useEffect(() => {
    let cancel = false;
    setOverviewLoading(true);
    api
      .get("/admin/analytics/overview")
      .then((res) => !cancel && setOverview(res.data))
      .catch(() => {})
      .finally(() => !cancel && setOverviewLoading(false));
    // Charts data
    setChartsLoading(true);
    Promise.all([
      api.get("/admin/analytics/timeseries", { params: { days: 14 } }),
      api.get("/admin/analytics/breakdown", { params: { field: "source", limit: 8, days: 30 } }),
      api.get("/admin/analytics/breakdown", { params: { field: "device", limit: 5, days: 30 } }),
      api.get("/admin/analytics/breakdown", { params: { field: "city", limit: 8, days: 30 } }),
      api.get("/admin/analytics/breakdown", { params: { field: "path", limit: 8, days: 30 } }),
    ])
      .then(([ts, src, dev, city, path]) => {
        if (cancel) return;
        setTimeseries(ts.data?.days || []);
        setBreakdownSource(src.data?.items || []);
        setBreakdownDevice(dev.data?.items || []);
        setBreakdownCity(city.data?.items || []);
        setBreakdownPath(path.data?.items || []);
      })
      .catch(() => {})
      .finally(() => !cancel && setChartsLoading(false));
    return () => {
      cancel = true;
    };
  }, []);

  useEffect(() => {
    let cancel = false;
    setLoading(true);
    api
      .get("/admin/analytics/events", { params })
      .then((res) => {
        if (cancel) return;
        setItems(res.data?.items || []);
        setTotal(res.data?.total || 0);
      })
      .catch((e) =>
        toast.error(e.response?.data?.detail || "No se pudieron cargar los eventos")
      )
      .finally(() => !cancel && setLoading(false));
    return () => {
      cancel = true;
    };
  }, [params]);

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const handleExport = async () => {
    try {
      const res = await api.get("/admin/analytics/events.csv", {
        params,
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `visitor_events_${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error("Error exportando CSV");
    }
  };

  const openDetail = async (ip) => {
    if (!ip) return;
    setDetailOpen(true);
    setDetail(null);
    try {
      const res = await api.get(`/admin/analytics/visitor/${encodeURIComponent(ip)}`);
      setDetail(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "No se pudo cargar el visitante");
      setDetailOpen(false);
    }
  };

  const updateFilter = (k, v) => {
    setPage(1);
    setFilters((f) => ({ ...f, [k]: v }));
  };

  return (
    <div className="space-y-6" data-testid="admin-analytics-tab">
      {/* Live pulse */}
      <div
        className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 p-4 rounded-2xl border border-emerald-200 bg-gradient-to-r from-emerald-50 to-white"
        data-testid="live-pulse-card"
      >
        <div className="flex items-center gap-3">
          <span className="relative inline-flex">
            <span className="absolute inline-flex h-3 w-3 rounded-full bg-emerald-400 opacity-70 animate-ping" />
            <span className="relative inline-flex rounded-full h-3 w-3 bg-emerald-500" />
          </span>
          <div>
            <p className="text-sm font-heading font-semibold text-emerald-900">
              {live.unique_ips_5m === 0
                ? "Sin visitas activas"
                : `${live.unique_ips_5m} ${
                    live.unique_ips_5m === 1 ? "persona" : "personas"
                  } en línea`}
              <span className="text-emerald-700/70 font-normal ml-2">
                ({live.events_5m} eventos · últimos 5 min)
              </span>
            </p>
            <p className="text-xs text-emerald-700/80">
              {live.unique_ips_60m} visitantes únicos en la última hora
            </p>
          </div>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => setNotifyOpen(true)}
          className="rounded-full"
          data-testid="notify-settings-btn"
        >
          {notifySettings.enabled ? (
            <Bell className="w-4 h-4 mr-1.5 text-emerald-600" />
          ) : (
            <BellOff className="w-4 h-4 mr-1.5 text-neutral-500" />
          )}
          Notificaciones
        </Button>
      </div>

      {/* Overview cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4">
        <StatCard
          icon={Users}
          label="Total visitas"
          value={overview?.total ?? (overviewLoading ? "…" : 0)}
          accent="bg-blue-100 text-blue-700"
        />
        <StatCard
          icon={CalendarDays}
          label="Hoy"
          value={overview?.today ?? 0}
          accent="bg-emerald-100 text-emerald-700"
        />
        <StatCard icon={Activity} label="Esta semana" value={overview?.week ?? 0} accent="bg-amber-100 text-amber-700" />
        <StatCard icon={TrendingUp} label="Este mes" value={overview?.month ?? 0} accent="bg-purple-100 text-purple-700" />
        <StatCard icon={Repeat} label="Recurrentes" value={overview?.returning ?? 0} accent="bg-pink-100 text-pink-700" />
        <StatCard icon={Globe2} label="IPs únicas" value={overview?.unique_ips ?? 0} accent="bg-sky-100 text-sky-700" />
        <StatCard icon={MapPin} label="Top ciudad" value={overview?.top_city || "—"} accent="bg-neutral-200 text-neutral-700" />
        <StatCard icon={ExternalLink} label="Top fuente" value={overview?.top_source || "—"} accent="bg-orange-100 text-orange-700" />
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="card-base lg:col-span-2" data-testid="chart-timeseries">
          <div className="flex items-center justify-between mb-4">
            <h4 className="font-heading font-semibold text-base">Visitas en los últimos 14 días</h4>
            {chartsLoading && <Loader2 className="w-4 h-4 animate-spin text-neutral-400" />}
          </div>
          <div className="h-56">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={timeseries}>
                <defs>
                  <linearGradient id="evGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#FF6B6B" stopOpacity={0.35} />
                    <stop offset="100%" stopColor="#FF6B6B" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" vertical={false} />
                <XAxis
                  dataKey="day"
                  tick={{ fontSize: 10, fill: "#6B7280" }}
                  tickFormatter={(d) => d.slice(5)}
                />
                <YAxis tick={{ fontSize: 10, fill: "#6B7280" }} allowDecimals={false} />
                <Tooltip />
                <Area
                  type="monotone"
                  dataKey="events"
                  name="Eventos"
                  stroke="#FF6B6B"
                  strokeWidth={2}
                  fill="url(#evGrad)"
                />
                <Area
                  type="monotone"
                  dataKey="unique_ips"
                  name="IPs únicas"
                  stroke="#0EA5E9"
                  strokeWidth={2}
                  fill="transparent"
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="card-base" data-testid="chart-source">
          <h4 className="font-heading font-semibold text-base mb-4">Top fuentes (30d)</h4>
          <BreakdownBars items={breakdownSource} colors={["#0EA5E9", "#22C55E", "#F59E0B", "#A855F7", "#EC4899", "#FF6B6B", "#14B8A6", "#6366F1"]} />
        </div>

        <div className="card-base" data-testid="chart-device">
          <h4 className="font-heading font-semibold text-base mb-4">Dispositivos (30d)</h4>
          <BreakdownBars items={breakdownDevice} colors={["#FF6B6B", "#0EA5E9", "#22C55E"]} />
        </div>

        <div className="card-base" data-testid="chart-city">
          <h4 className="font-heading font-semibold text-base mb-4">Top ciudades (30d)</h4>
          <BreakdownBars items={breakdownCity} colors={["#6366F1"]} />
        </div>

        <div className="card-base" data-testid="chart-path">
          <h4 className="font-heading font-semibold text-base mb-4">Páginas más visitadas (30d)</h4>
          <BreakdownBars items={breakdownPath} colors={["#111111"]} truncate />
        </div>
      </div>

      {/* Filters + actions */}
      <div className="card-base">
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
          <div>
            <Label className="text-[11px] uppercase tracking-wide text-neutral-500">Desde</Label>
            <Input
              type="datetime-local"
              value={filters.start}
              onChange={(e) => updateFilter("start", e.target.value)}
              data-testid="analytics-filter-start"
            />
          </div>
          <div>
            <Label className="text-[11px] uppercase tracking-wide text-neutral-500">Hasta</Label>
            <Input
              type="datetime-local"
              value={filters.end}
              onChange={(e) => updateFilter("end", e.target.value)}
              data-testid="analytics-filter-end"
            />
          </div>
          <div>
            <Label className="text-[11px] uppercase tracking-wide text-neutral-500">País</Label>
            <Input
              placeholder="México"
              value={filters.country}
              onChange={(e) => updateFilter("country", e.target.value)}
              data-testid="analytics-filter-country"
            />
          </div>
          <div>
            <Label className="text-[11px] uppercase tracking-wide text-neutral-500">Fuente</Label>
            <Select
              value={filters.source || "all"}
              onValueChange={(v) => updateFilter("source", v === "all" ? "" : v)}
            >
              <SelectTrigger data-testid="analytics-filter-source">
                <SelectValue placeholder="Todas" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todas</SelectItem>
                {SOURCE_OPTIONS.map((s) => (
                  <SelectItem key={s} value={s}>
                    {s}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-[11px] uppercase tracking-wide text-neutral-500">Dispositivo</Label>
            <Select
              value={filters.device || "all"}
              onValueChange={(v) => updateFilter("device", v === "all" ? "" : v)}
            >
              <SelectTrigger data-testid="analytics-filter-device">
                <SelectValue placeholder="Todos" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos</SelectItem>
                {DEVICE_OPTIONS.map((d) => (
                  <SelectItem key={d} value={d}>
                    {d}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-[11px] uppercase tracking-wide text-neutral-500">Audiencia</Label>
            <Select value={filters.audience} onValueChange={(v) => updateFilter("audience", v)}>
              <SelectTrigger data-testid="analytics-filter-audience">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos</SelectItem>
                <SelectItem value="logged">Solo logueados</SelectItem>
                <SelectItem value="anonymous">Solo anónimos</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="flex flex-col sm:flex-row gap-3 mt-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400" />
            <Input
              placeholder="Buscar IP, ruta, email o nombre…"
              className="pl-9"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") updateFilter("search", searchInput.trim());
              }}
              data-testid="analytics-search-input"
            />
          </div>
          <Button
            variant="outline"
            onClick={() => updateFilter("search", searchInput.trim())}
            data-testid="analytics-search-btn"
          >
            Buscar
          </Button>
          <Button
            variant="outline"
            onClick={() => {
              setSearchInput("");
              setPage(1);
              setFilters({
                start: "",
                end: "",
                country: "",
                source: "",
                device: "",
                audience: "all",
                search: "",
              });
            }}
            data-testid="analytics-reset-btn"
          >
            Limpiar
          </Button>
          <Button onClick={handleExport} className="gap-1.5" data-testid="analytics-export-btn">
            <Download className="w-4 h-4" /> Exportar CSV
          </Button>
        </div>
      </div>

      {/* Events table */}
      <div className="card-base p-0 overflow-hidden">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Fecha</TableHead>
                <TableHead>IP</TableHead>
                <TableHead>País</TableHead>
                <TableHead>Estado</TableHead>
                <TableHead>Ciudad</TableHead>
                <TableHead>Página</TableHead>
                <TableHead>Dispositivo</TableHead>
                <TableHead>Browser</TableHead>
                <TableHead>Fuente</TableHead>
                <TableHead>Usuario</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={10} className="text-center py-10">
                    <Loader2 className="w-5 h-5 animate-spin inline" />
                  </TableCell>
                </TableRow>
              ) : items.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={10} className="text-center py-10 text-neutral-500">
                    Sin eventos para los filtros seleccionados.
                  </TableCell>
                </TableRow>
              ) : (
                items.map((e) => (
                  <TableRow
                    key={e.event_id}
                    className="cursor-pointer hover:bg-neutral-50"
                    onClick={() => openDetail(e.ip)}
                    data-testid={`analytics-row-${e.event_id}`}
                  >
                    <TableCell className="text-xs whitespace-nowrap">{fmtTs(e.ts)}</TableCell>
                    <TableCell className="font-mono text-xs">{e.ip}</TableCell>
                    <TableCell className="text-xs">{e.country || "—"}</TableCell>
                    <TableCell className="text-xs">{e.region || "—"}</TableCell>
                    <TableCell className="text-xs">{e.city || "—"}</TableCell>
                    <TableCell className="text-xs max-w-[180px] truncate">{e.path || "—"}</TableCell>
                    <TableCell className="text-xs capitalize">{e.device}</TableCell>
                    <TableCell className="text-xs">{e.browser}</TableCell>
                    <TableCell>
                      <Badge variant="outline" className="text-[10px] capitalize">
                        {e.source || "—"}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-xs">
                      {e.user_email ? (
                        <span className="text-terracotta font-medium">{e.user_email}</span>
                      ) : (
                        <span className="text-neutral-400">anónimo</span>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>

        <div className="flex items-center justify-between p-4 border-t border-neutral-100 text-sm">
          <span className="text-neutral-500">
            {total} eventos · página {page} de {totalPages}
          </span>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={page === 1 || loading}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              data-testid="analytics-prev-page"
            >
              <ChevronLeft className="w-4 h-4" /> Anterior
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled={page >= totalPages || loading}
              onClick={() => setPage((p) => p + 1)}
              data-testid="analytics-next-page"
            >
              Siguiente <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </div>

      {/* Visitor detail sheet */}
      <Sheet open={detailOpen} onOpenChange={setDetailOpen}>
        <SheetContent
          side="right"
          className="w-full sm:max-w-xl overflow-y-auto"
          data-testid="analytics-detail-sheet"
        >
          <SheetHeader>
            <SheetTitle>Detalle del visitante</SheetTitle>
          </SheetHeader>
          {!detail ? (
            <div className="py-10 text-center text-neutral-500">
              <Loader2 className="w-5 h-5 animate-spin inline" />
            </div>
          ) : (
            <div className="space-y-5 mt-4">
              <div className="grid grid-cols-2 gap-3">
                <div className="p-3 rounded-xl border border-neutral-200">
                  <p className="text-[11px] uppercase tracking-wide text-neutral-500">IP</p>
                  <p className="font-mono text-sm">{detail.ip}</p>
                </div>
                <div className="p-3 rounded-xl border border-neutral-200">
                  <p className="text-[11px] uppercase tracking-wide text-neutral-500">Total eventos</p>
                  <p className="font-heading font-bold text-lg">{detail.total_events}</p>
                </div>
                <div className="p-3 rounded-xl border border-neutral-200">
                  <p className="text-[11px] uppercase tracking-wide text-neutral-500">Primera visita</p>
                  <p className="text-sm">{fmtTs(detail.first_seen)}</p>
                </div>
                <div className="p-3 rounded-xl border border-neutral-200">
                  <p className="text-[11px] uppercase tracking-wide text-neutral-500">Última visita</p>
                  <p className="text-sm">{fmtTs(detail.last_seen)}</p>
                </div>
              </div>

              <div className="p-4 rounded-2xl bg-neutral-50 border border-neutral-200 text-sm space-y-1">
                <p>
                  <span className="text-neutral-500">Ubicación: </span>
                  {[detail.city, detail.region, detail.country].filter(Boolean).join(", ") || "—"}
                </p>
                <p>
                  <span className="text-neutral-500">Dispositivo: </span>
                  {detail.device} · {detail.browser} · {detail.os}
                </p>
                <p className="break-all">
                  <span className="text-neutral-500">User-Agent: </span>
                  <span className="font-mono text-[11px]">{detail.ua}</span>
                </p>
                {detail.linked_user_ids?.length > 0 && (
                  <p>
                    <span className="text-neutral-500">Usuario vinculado: </span>
                    {detail.linked_user_ids.map((uid) => (
                      <a
                        key={uid}
                        href={`/u/${uid}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-terracotta font-medium hover:underline mr-2"
                      >
                        {uid}
                      </a>
                    ))}
                  </p>
                )}
                {detail.sources?.length > 0 && (
                  <p>
                    <span className="text-neutral-500">Fuentes: </span>
                    {detail.sources.join(", ")}
                  </p>
                )}
              </div>

              <div>
                <h4 className="font-heading font-semibold mb-2 text-sm">
                  Páginas más visitadas
                </h4>
                <ul className="text-sm space-y-1 max-h-48 overflow-y-auto pr-1">
                  {detail.top_paths?.map((p) => (
                    <li key={p} className="text-neutral-700 truncate">
                      {p}
                    </li>
                  ))}
                </ul>
              </div>

              <div>
                <h4 className="font-heading font-semibold mb-2 text-sm">
                  Historial reciente
                </h4>
                <ul className="text-xs space-y-1 max-h-72 overflow-y-auto pr-1">
                  {detail.events?.slice(0, 100).map((e) => (
                    <li
                      key={e.event_id}
                      className="flex justify-between gap-2 border-b border-neutral-100 py-1"
                    >
                      <span className="text-neutral-500 whitespace-nowrap">{fmtTs(e.ts)}</span>
                      <span className="truncate flex-1 text-right">{e.path}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          )}
        </SheetContent>
      </Sheet>

      {/* Notification settings dialog */}
      <Dialog open={notifyOpen} onOpenChange={setNotifyOpen}>
        <DialogContent className="sm:max-w-md" data-testid="notify-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Settings2 className="w-5 h-5 text-emerald-600" />
              Notificaciones de visitantes
            </DialogTitle>
            <DialogDescription>
              Recibe un correo cuando una nueva IP entra a Rivones (rate-limit
              configurable). El correo llega a tu celular como push de tu app de
              email.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div className="flex items-center justify-between p-3 rounded-xl border border-neutral-200">
              <div>
                <p className="font-semibold text-sm">Activar alertas</p>
                <p className="text-xs text-neutral-500">
                  Solo te avisamos cuando es una IP nueva.
                </p>
              </div>
              <Switch
                checked={notifySettings.enabled}
                onCheckedChange={(v) =>
                  setNotifySettings((s) => ({ ...s, enabled: v }))
                }
                data-testid="notify-enabled-switch"
              />
            </div>
            <div>
              <Label htmlFor="notify-email" className="text-sm font-semibold">
                Correo destino
              </Label>
              <Input
                id="notify-email"
                type="email"
                placeholder="tu-correo@ejemplo.com"
                value={notifySettings.email}
                onChange={(e) =>
                  setNotifySettings((s) => ({ ...s, email: e.target.value }))
                }
                data-testid="notify-email-input"
                className="mt-1.5"
              />
              <p className="text-[11px] text-neutral-500 mt-1">
                Configura tu app de email (Gmail / Apple Mail) para mostrar push en
                tu celular.
              </p>
            </div>
            <div>
              <Label className="text-sm font-semibold">
                Intervalo mínimo entre alertas
              </Label>
              <Select
                value={String(notifySettings.min_interval_minutes || 5)}
                onValueChange={(v) =>
                  setNotifySettings((s) => ({ ...s, min_interval_minutes: Number(v) }))
                }
              >
                <SelectTrigger
                  data-testid="notify-interval-select"
                  className="mt-1.5"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="1">Cada 1 minuto</SelectItem>
                  <SelectItem value="5">Cada 5 minutos (recomendado)</SelectItem>
                  <SelectItem value="15">Cada 15 minutos</SelectItem>
                  <SelectItem value="30">Cada 30 minutos</SelectItem>
                  <SelectItem value="60">Cada hora</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-[11px] text-neutral-500 mt-1">
                Para evitar spam si llegan muchos visitantes seguidos.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setNotifyOpen(false)}
              disabled={notifySaving}
            >
              Cancelar
            </Button>
            <Button
              onClick={saveNotifySettings}
              disabled={notifySaving}
              data-testid="notify-save-btn"
              className="bg-[#111111] hover:bg-neutral-800 text-white"
            >
              {notifySaving ? (
                <Loader2 className="w-4 h-4 animate-spin mr-1.5" />
              ) : null}
              Guardar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
